import React from 'react'
import SectionType from 'models/types/ts/sectionType'
import {
    Options,
    documentToReactComponents,
} from '@contentful/rich-text-react-renderer'
import { Tabs } from '@mantine/core'
import { useSelector } from 'react-redux'
import { GlobalState } from 'types'
import getConfig, { isServer } from 'config/web'
import { useQuery } from '@apollo/client'
import { TAB_CONTENT_BY_TABS_REF } from 'queries/tab-content-by-tabs-ref/query'

import { INLINES } from '@contentful/rich-text-types'
import { getItemsMap } from 'pages/news/components/newsDetail/components/rich-text/getRenderOptions'
import { ChartContent, LeaderBoardChart } from './LeaderboardChart'
import classes from './Leaderboards.module.scss'

interface Props {
    section: SectionType
}

interface ChartContentEntry extends ChartContent {
    __typename: 'Chart'
}

interface LabelEntry {
    __typename: 'Label'
    text: string
    color: string
}

type BasicEntryType = ChartContentEntry | LabelEntry

const {
    contentful: { preview },
} = getConfig()

const getRenderOptions = (links): Options => {
    const items = getItemsMap(links)

    return {
        renderNode: {
            [INLINES.EMBEDDED_ENTRY]: (node, children) => {
                const sysId = node?.data?.target?.sys?.id // TODO possible null reference error

                if (!sysId) return null

                const entry = items.entries.get(sysId) as BasicEntryType

                if (!entry) return null
                if (entry.__typename === 'Chart') {
                    const {
                        height,
                        options,
                        data,
                        fromDateTime,
                        toDateTime,
                        leaderboardType,
                        title,
                        subtitle,
                    } = entry

                    return (
                        <LeaderBoardChart
                            height={height}
                            options={options}
                            data={data}
                            fromDateTime={fromDateTime}
                            toDateTime={toDateTime}
                            leaderboardType={leaderboardType}
                            title={title}
                            subtitle={subtitle}
                        />
                    )
                }
                if (entry.__typename === 'Label') {
                    const { text, color } = entry
                    return (
                        <span style={{ color, fontSize: 16, fontWeight: 500 }}>
                            {text}
                        </span>
                    )
                }
                return null
            },
        },
        renderText: (text) => {
            return (
                <span
                    style={{ fontSize: 16, fontWeight: 500 }}
                    // We want to render nbsp
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{ __html: text }}
                />
            )
            return text
        },
    }
}

const Leaderboards = ({ section }: Props): JSX.Element => {
    const tabs = section.contentCollection.items.find(
        (item) => item.__typename === 'Tabs'
    )
    const locale = useSelector((state: GlobalState) => state.language)
    const fetchPolicy = isServer ? 'cache-and-network' : 'cache-first'

    const { loading, error, data } = useQuery(TAB_CONTENT_BY_TABS_REF, {
        variables: { id: tabs?.sys?.id, locale, preview },
        fetchPolicy,
        errorPolicy: 'ignore',
    })

    if (loading) {
        return <></>
    }

    return (
        <div className={classes.leaderBoardsContainer}>
            <h2 className={classes.sectionTitle}>{section.name}</h2>
            <Tabs keepMounted={false} defaultValue="0">
                <Tabs.List className={classes.tabsList}>
                    {(tabs?.tabsCollection?.items || []).map(
                        ({ title, sys: { id } }, index) => {
                            return (
                                <Tabs.Tab
                                    className={classes.tabs}
                                    value={String(index)}
                                    key={id}
                                >
                                    {title}
                                </Tabs.Tab>
                            )
                        }
                    )}
                </Tabs.List>
                {loading ? (
                    <></>
                ) : (
                    (data.tabs.tabsCollection.items || []).map(
                        ({ content: { json, links } }, index) => {
                            return (
                                <Tabs.Panel
                                    value={String(index)}
                                    key={JSON.stringify(data)}
                                >
                                    {documentToReactComponents(
                                        json,
                                        getRenderOptions(links)
                                    )}
                                </Tabs.Panel>
                            )
                        }
                    )
                )}
            </Tabs>
        </div>
    )
}

export default Leaderboards
